import React, { useContext, useEffect, useState, useRef, memo, useCallback } from "react";
import { message } from "antd";
import dayjs from "dayjs";
import PropTypes from "prop-types";

import {
	CreateTransactionModal,
	BankTransactionsSection,
	UploadTransactionsModal,
	ConvertTemplateModal,	
} from "./components";

import { edit_bank_transactions, get_bank_transactions } from "../../services";
import { BankAccountContext } from "../../contexts";
import { DATE_DEFAULT_FORMAT, filterTransactionsToShow, formatDateStampToString } from "./utils";
import { ClassificationsContext } from "../../contexts/ClassificationsContext";

export const BankTransactions = memo(({	
	token, 
	company, 
	updateData = () => {},
}) => {
	const modalConvertTemplateRef = useRef(null);
	const modalUploadTransactionsRef = useRef(null);
	const modalCreateTransactionRef = useRef(null);
					
	const { bankAccountSelected, updateBasePointsFromBankAccountSelected, setBankAccountSelected, setEditedTransactions } = useContext(BankAccountContext);
	const { fetchClassificationForBankTransactions } = useContext(ClassificationsContext);
	const [ bankTransactionsData, setBankTransactionsData ] = useState([]);
	const [ backupBankTransactionsData, setBackupBankTransactionsData ] = useState([]);
	const [ loading, setLoading ] = useState(true);	
	
	const [ lastMonthsDetected, setLastMonthsDetected ] = useState({
		firstMonthDetected: "",
		lastMonthDetected: "",
	});
	
	const clearFileListRef = useRef(null);

	const clearFileList = () => {
		if (clearFileListRef.current) {
			clearFileListRef.current();
		}
	};
		
	useEffect(() => {
		const fetchData = async () => {
			try {
				setLoading(true);
				const res = await get_bank_transactions(token, "default", { bank_account_id: bankAccountSelected.id });

				if(!res){
					setLoading(false);
					setLastMonthsDetected({firstMonthDetected: "", lastMonthDetected: ""});
					return;
				}

				const { transactions, message: messageRes, dateFounded = "" } = res;

				const dataFormatted = transactions.map((data) => {
					return {
						...data,
						date: `${formatDateStampToString(data.date)}`,
						last_update_at: `${formatDateStampToString(data.last_update_at)}`,
					};
				});

				setBankTransactionsData(dataFormatted);
				setBackupBankTransactionsData(dataFormatted);
				setLastMonthsDetected({ firstMonthDetected: dateFounded, lastMonthDetected: "" });				
				message.info(messageRes);

				// By the moment the classifications will be consulted
				await fetchClassificationForBankTransactions(company.id);
				
			} catch (err) {
				message.error(err.response.data);				
			} finally {
				setLoading(false);
			}
		};

		if(bankAccountSelected === null) return;

		if(backupBankTransactionsData.length !== 0) return;
		
		fetchData(); // ! RECOMENDED to comment when the table is getted edited.
	}, [bankAccountSelected]);
		
	const createTransaction = async (newTransaction) => {
		const transactionToSend = {
			...newTransaction,
			bank_account_id: bankAccountSelected.id,
			client_id: newTransaction.position === "in" ? newTransaction.client_id : null,
			supplier_id: newTransaction.position === "out" ? newTransaction.supplier_id : null,
		};
		
		try {			
			setLoading(true);
			
			const res = await edit_bank_transactions(token, "create", transactionToSend);
			if(res){
				const { id, updatedBasePoints, updatedTemporalBasePoints } = res;

				const dataToSet = {
					id,
					...transactionToSend,
					state: "unverified",
					date: transactionToSend.date,
					amount_original: {
						amount: newTransaction.amount,
						position: newTransaction.position,
					}
				};

				const transactionsToShow = filterTransactionsToShow([dataToSet], lastMonthsDetected);
				
				// Convert to Strings the Dates
				const transactionsTransformed = transactionsToShow.map((transaction) => {
					const formatedDate = transaction.date.format(DATE_DEFAULT_FORMAT);
					
					return {
						...transaction,
						date: formatedDate,
						last_update_at: formatedDate,
					};
				});
				
				setBankTransactionsData([...transactionsTransformed, ...bankTransactionsData]);
				setBackupBankTransactionsData([...transactionsTransformed, ...backupBankTransactionsData]);
				message.success("Nueva Transacción Creada con Exito");
				modalCreateTransactionRef.current?.openModal(false);				
				updateBasePointsFromBankAccountSelected(updatedBasePoints, updatedTemporalBasePoints, updateData);
			}
		} catch (err) {
			message.error("Un error acaba de suceder");
		}
		setLoading(false);
	};

	// file
	const uploadTransaction = async () => {
		setLoading(false);
	};

	const fetchFilterTransactions = async(value) => {
		const fetchTransactionsFiltered = async (data) => {			
			setLoading(true);			
			setEditedTransactions([]);
			const res = await get_bank_transactions(token, "personalized", data);
			
			if(res){
				const { transactions, message: messageRes } = res;

				const dataFormatted = transactions.map((data) => {
					return {
						...data,
						date: `${formatDateStampToString(data.date)}`,
						last_update_at: `${formatDateStampToString(data.last_update_at)}`,
					};
				});

				setBankTransactionsData(dataFormatted);
				setBackupBankTransactionsData(dataFormatted);
				message.info(messageRes);
				setLoading(false);
				return true;
			}

			setLoading(false);
			return false;						
		};
		
		const { month = null, months = null } = value;
	
		if(month){
			const formattedMonth = `${month.$y}-${String(month.$M + 1).padStart(2, "0")}`;
			const sucessfull = await fetchTransactionsFiltered({ bank_account_id: bankAccountSelected.id, single: formattedMonth });
			setLastMonthsDetected({ 
				firstMonthDetected: sucessfull ? formattedMonth : "",
				lastMonthDetected: ""
			});
			return;
		}

		if(months){
			const formattedMonths = months.map((m) => `${m.$y}-${String(m.$M + 1).padStart(2, "0")}`);
			const sucessfull = await fetchTransactionsFiltered({ bank_account_id: bankAccountSelected.id, range: formattedMonths });
			setLastMonthsDetected({
				firstMonthDetected: sucessfull ? formattedMonths[0] : "", 
				lastMonthDetected: sucessfull ? formattedMonths[1] : "",
			});
			return;
		}
	};

	const updateSingleRegister = useCallback((attribute, value, record) => {
		setBankTransactionsData((prevData) =>
			prevData.map((item) =>
				item.id === record.id ? { ...item, [attribute]: value } : item
			)
		);
	
		setEditedTransactions((prevEdits) => ({
			...prevEdits,
			[record.id]: { ...prevEdits[record.id], [attribute]: value },
		}));
	}, []);
		
	return (
		<>
			<BankTransactionsSection
				token={token}
				company={company}
				bankTransactionsData={bankTransactionsData}
				backupBankTransactionsData={backupBankTransactionsData}				
				loading={loading}
				lastMonthsDetected={lastMonthsDetected}				
				onUpdateSingleRegister={updateSingleRegister}
				onBankTransactionsData={(value) => setBankTransactionsData(value)}
				onBackupBankTransactionsData={(value) => setBackupBankTransactionsData(value)}
				onCloseBankAccount={(value) => {
					setLoading(true);
					setBankTransactionsData([]);
					setBackupBankTransactionsData([]);
					setBankAccountSelected(value);
				}}
				onOpenCreateTransaction={(value) => modalCreateTransactionRef.current?.openModal(value)}					
				onOpenConvertTemplate={(value) => modalConvertTemplateRef.current?.openModal(value)}				
				onFilterTransactionsProps={fetchFilterTransactions}
				updateData={updateData}
			/>

			<CreateTransactionModal
				ref={modalCreateTransactionRef}
				token={token}
				company={company}
				onCreateTransaction={createTransaction}
				loading={loading}				
			/>

			<ConvertTemplateModal				
				ref={modalConvertTemplateRef}
				bankAccountSelected={bankAccountSelected} 
				onOpenUploadTransaction={(value) => modalUploadTransactionsRef.current?.openModal(value)}
				onFileContent={(value) => modalUploadTransactionsRef.current?.onSetFileContent(value)}
				onClearFileList={(callback) => (clearFileListRef.current = callback)}
			/>

			<UploadTransactionsModal
				ref={modalUploadTransactionsRef}
				token={token}
				bankAccountSelected={bankAccountSelected}				
				onOpenUploadTransaction={(value) => modalUploadTransactionsRef.current?.openModal(value)}
				onOpenConvertTemplate={(value) => modalConvertTemplateRef.current?.openModal(value)}
				onUploadTransaction={uploadTransaction}
				loading={loading}
				setLoading={setLoading}
				onEmitNewTransactions={({newBankTransactions, updatedBasePoints, updatedTemporalBasePoints}) => {
					const transactionsWithNormalizedDate = newBankTransactions.map((transaction) => {
						const { date, last_update_at } = transaction;
						
						const convertedDate = formatDateStampToString(date);
						const convertedLastUpdateAt = formatDateStampToString(last_update_at);
						
						return {
							...transaction,
							date: dayjs(convertedDate, DATE_DEFAULT_FORMAT),
							last_update_at: dayjs(convertedLastUpdateAt, DATE_DEFAULT_FORMAT),
						};
					});
									
					const transactionsFiltered = filterTransactionsToShow(
						transactionsWithNormalizedDate,
						lastMonthsDetected
					);

					const transactionsTransformed = transactionsFiltered.map((transaction) => {		
						const { date, last_update_at } = transaction;
																
						return {
							...transaction,
							date: date.format(DATE_DEFAULT_FORMAT),
							last_update_at: last_update_at.format(DATE_DEFAULT_FORMAT),
						};
					});
												
					setBankTransactionsData([...transactionsTransformed, ...bankTransactionsData]);
					setBackupBankTransactionsData([...transactionsTransformed, ...backupBankTransactionsData]);
				
					updateBasePointsFromBankAccountSelected(updatedBasePoints, updatedTemporalBasePoints, updateData);
				}}
					
				onClearFileList={clearFileList}
			/>						
		</> 
	);
});

BankTransactions.displayName = "BankTransactions";

BankTransactions.propTypes = {
	token: PropTypes.string.isRequired,
	company: PropTypes.object.isRequired,
	updateData: PropTypes.func.isRequired,
};
